import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useLocation } from "react-router-dom";

import Client from "../components/Client";
import Config from "../components/ClientConfig";
import DropDown from "../components/DropDown";
import { useDataStore } from "../dataStore";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import IconButton from "@mui/material/IconButton";

const pageShellSx = {
  px: { xs: 0, sm: 2 },
  py: { xs: 0.5, sm: 2.5 },
  display: "flex",
  justifyContent: "center",
};
const pageSurfaceSx = {
  borderRadius: "8px",
  overflow: "hidden",
  border: "1px solid rgba(124,45,18,0.10)",
  bgcolor: "rgba(255,255,255,0.94)",
  boxShadow: "0 20px 48px rgba(33,20,12,0.10)",
};
const headerSx = {
  p: { xs: 1.5, sm: 2.5, md: 3 },
  color: "#431407",
  background:
    "linear-gradient(135deg, rgba(255,250,243,0.98) 0%, rgba(255,237,213,0.96) 54%, rgba(254,215,170,0.88) 100%)",
  borderBottom: "1px solid rgba(124,45,18,0.10)",
};
const headerIconSx = {
  width: 44,
  height: 44,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#111111",
  bgcolor: "#F7B500",
  border: "1px solid rgba(255,255,255,0.20)",
  flexShrink: 0,
};
const stepCardSx = {
  borderRadius: "8px",
  borderColor: "rgba(124,45,18,0.10)",
  bgcolor: "rgba(255,255,255,0.92)",
};
const primaryButtonSx = {
  borderRadius: "8px",
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
};
const secondaryButtonSx = {
  borderRadius: "8px",
  fontWeight: 850,
  textTransform: "none",
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function InvoiceGenerator() {
  const query = useQuery();
  const { languageCode } = useDataStore();

  // ---------------- i18n (single-file) ----------------
  const TEXT = useMemo(
    () => ({
      ro: {
        invoice: "Factură",
        subtitle: "Validează comanda, apoi completează datele firmei și descarcă PDF-ul",
        validated: "Validat",
        notValidated: "Nevalidat",

        clear: "Șterge",
        validate: "Validează",
        working: "Se procesează…",

        step1Title: "1) Validare comandă",
        step1Desc: "Introdu codul, suma și data. Dacă factura există, vei primi link-ul de descărcare.",
        orderCode: "Cod comandă",
        amount: "Sumă",
        orderDate: "Data comenzii",

        step1InfoInvoiceExists: "Factura este deja generată. Folosește link-ul de mai sus pentru descărcare.",
        step1InfoValidated: "Comanda a fost validată. Poți completa acum datele firmei.",

        step2Title: "2) Firmă",
        required: "Obligatoriu: Nume, CUI, Stradă",
        companyName: "Nume firmă",
        cui: "CUI",
        lookupCompany: "Caută firmă",
        regNo: "Nr. Reg. Com.",
        street: "Stradă",
        no: "Nr.",
        country: "Țară",
        county: "Județ",
        city: "Oraș",
        postal: "Cod poștal",
        email: "Email (opțional)",
        phone: "Telefon (opțional)",
        geoHint: "Țară → Județ → Oraș (Orașul completează automat codul poștal dacă este disponibil)",

        step3Title: "3) Generare",
        readyToGenerate: "Gata pentru generarea facturii.",
        completeRequired: "Completează câmpurile obligatorii ale firmei.",
        generateInvoice: "Generează factura",
        clearResult: "Șterge rezultatul",
        warnFill: "Completează: Nume firmă, CUI, Stradă.",

        downloadPdf: "Descarcă PDF",
        linkExpires: (mins) => `Link-ul expiră în ${mins} minute.`,

        // errors
        errValidate: "Nu s-a putut valida comanda. Verifică codul, suma și data.",
        errGenerate: "Nu s-a putut genera factura. Verifică datele și încearcă din nou.",
        errOrderNotVerified: "Comanda nu a putut fi verificată.",
        errCompanyLookup: "Căutarea firmei a eșuat.",

        // misc
        invoiceReadyLabel: "Factura",
        readyWord: "pregătită",
        countries: { country061: "România" },
      },

      en: {
        invoice: "Invoice",
        subtitle: "Validate order, then fill company, then download PDF",
        validated: "Validated",
        notValidated: "Not validated",

        clear: "Clear",
        validate: "Validate",
        working: "Working…",

        step1Title: "1) Validate order",
        step1Desc: "Enter code, amount, date. If invoice exists, you’ll get a download link.",
        orderCode: "Order code",
        amount: "Amount",
        orderDate: "Order date",

        step1InfoInvoiceExists: "Invoice already generated. Use the download link above.",
        step1InfoValidated: "Order validated. Company fields are now available.",

        step2Title: "2) Company",
        required: "Required: Name, CUI, Street",
        companyName: "Company name",
        cui: "CUI",
        lookupCompany: "Lookup company",
        regNo: "Reg. No (ruid)",
        street: "Street",
        no: "No.",
        country: "Country",
        county: "County",
        city: "City",
        postal: "Postal code",
        email: "Email (optional)",
        phone: "Phone (optional)",
        geoHint: "Country → County → City (City autofills postal code when available)",

        step3Title: "3) Generate",
        readyToGenerate: "Ready to generate invoice.",
        completeRequired: "Complete required company fields.",
        generateInvoice: "Generate invoice",
        clearResult: "Clear result",
        warnFill: "Fill: Company name, CUI, Street.",

        downloadPdf: "Download PDF",
        linkExpires: (mins) => `Link expires in ${mins} minutes.`,

        // errors
        errValidate: "Could not validate order. Please check code, amount and date.",
        errGenerate: "Could not generate invoice. Please verify details and try again.",
        errOrderNotVerified: "Order could not be verified.",
        errCompanyLookup: "Company lookup failed.",

        // misc
        invoiceReadyLabel: "Invoice",
        readyWord: "ready",
        countries: { country061: "Romania" },
      },
    }),
    []
  );
  const lang = String(languageCode || "RO").toLowerCase() === "en" ? "en" : "ro";
  const t = TEXT[lang] || TEXT.ro;

  const INITIAL_COMPANY = useMemo(
    () => ({
      name: "",
      cuid: "",
      ruid: "",
      phone: "",
      email: "",
      address: {
        postalCode: "",
        street: "",
        number: "",
        entrance: "",
        local: "",
        floor: "",
        complement: "",
        city: {
          iref: "",
          name: "City",
          county: {
            iref: "",
            name: "County",
            country: { iref: "country061", name: "Country" },
          },
        },
      },
    }),
    []
  );

  const lastOrderCodeRef = useRef("");
  const autoValidatedRef = useRef(false);
  const [, setAutoLoading] = useState(false);

  // DEV-only: silence ONLY the ResizeObserver loop spam for this page while mounted
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    const originalConsoleError = console.error;
    console.error = (...args) => {
      const msg = args?.[0]?.toString?.() || "";
      if (msg.includes("ResizeObserver loop completed with undelivered notifications")) return;
      originalConsoleError(...args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  // Client instance
  const clientRef = useRef(null);
  if (!clientRef.current) clientRef.current = new Client();
  const client = clientRef.current;

  // Step 1 inputs
  const [orderCode, setOrderCode] = useState("");
  const [amount, setAmount] = useState("");
  const [orderDate, setOrderDate] = useState(""); // YYYY-MM-DD

  // State
  const [identified, setIdentified] = useState(false);
  const [invoiceExists, setInvoiceExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [company, setCompany] = useState(INITIAL_COMPANY);

  // Geo options
  const [counties, setCounties] = useState({});
  const [cities, setCities] = useState({});

  // Result
  const [invoiceSerial, setInvoiceSerial] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [expiresInSeconds, setExpiresInSeconds] = useState(null);

  const showDownload = Boolean(downloadUrl);

  const canValidate = Boolean(orderCode.trim()) && Boolean(amount.trim()) && Boolean(orderDate.trim());

  // Progressive disclosure:
  // show company only after order validated AND invoice doesn't already exist
  const showCompanyStep = identified && !invoiceExists;

  const canGenerate =
    showCompanyStep &&
    company.name.trim().length > 1 &&
    company.cuid.trim().length > 1 &&
    company.address.street.trim().length > 2;

  // Selected IDs for dropdowns
  const countryId = company?.address?.city?.county?.country?.iref || "";
  const countyId = company?.address?.city?.county?.iref || "";
  const cityId = company?.address?.city?.iref || "";

  // Countries
  const countriesOptions = useMemo(() => t.countries, [t]);

  const countiesOptions = useMemo(() => {
    const out = {};
    Object.keys(counties || {}).forEach((k) => {
      const el = counties[k];
      if (el?.iref) out[el.iref] = el.name;
    });
    return out;
  }, [counties]);

  const citiesOptions = useMemo(() => {
    const out = {};
    Object.keys(cities || {}).forEach((k) => {
      const el = cities[k];
      if (el?.iref) out[el.iref] = el.name;
    });
    return out;
  }, [cities]);

  function clearResult() {
    setInvoiceSerial("");
    setDownloadUrl("");
    setExpiresInSeconds(null);
  }

  function clearAllValidation() {
    setIdentified(false);
    setInvoiceExists(false);
    setErrorMsg("");
    clearResult();
  }

  function extract(res) {
    const ok = String(res?.status || "").toUpperCase() === "OK";
    const data = res?.data || {};
    return { ok, data };
  }

  function validateInvoicePublic(payload) {
    const url = Config.API_URL_ORDERS + "invoice/validate/";
    return client.pushData(url, payload);
  }

  function generateInvoicePublic(payload) {
    const url = Config.API_URL_ORDERS + "invoice/generate/";
    return client.pushData(url, payload);
  }

  function resetCompanyOnly() {
    setCompany(INITIAL_COMPANY);
    setCounties({});
    setCities({});
  }

  // URL autofill + auto validate
  useEffect(() => {
    const qCode = query.get("code") || query.get("orderCode");
    const qAmount =
      query.get("amount") ||
      query.get("totalAmount") ||
      query.get("sum") ||
      query.get("total");
    const qDate = query.get("date") || query.get("orderDate");

    if (qCode && !orderCode) setOrderCode(qCode);
    if (qAmount && !amount) setAmount(qAmount);
    if (qDate && !orderDate) setOrderDate(qDate);

    const finalCode = (orderCode || qCode || "").trim();
    const finalAmount = (amount || qAmount || "").trim();
    const finalDate = (orderDate || qDate || "").trim();

    const hasAll = finalCode && finalAmount && finalDate;

    if (hasAll && !autoValidatedRef.current) {
      autoValidatedRef.current = true;

      (async () => {
        setErrorMsg("");
        clearResult();
        setIdentified(false);
        setInvoiceExists(false);

        setAutoLoading(true);
        try {
          const payload = {
            order: {
              code: finalCode,
              totalAmount: finalAmount,
              orderDate: finalDate,
            },
          };

          const res = validateInvoicePublic(payload);
          const { ok, data } = extract(res);
          if (!ok) throw new Error("INVALID");

          setIdentified(Boolean(data?.identified ?? true));
          lastOrderCodeRef.current = finalCode;

          const url = data?.downloadURL || data?.invoiceURL || "";
          if (data?.invoiceExists || url) {
            if (url) {
              setInvoiceExists(true);
              setInvoiceSerial(data?.invoice || "");
              setDownloadUrl(url);
              setExpiresInSeconds(data?.expiresInSeconds ?? null);
            }
          }
        } catch (_) {
          setErrorMsg(t.errValidate);
        } finally {
          setAutoLoading(false);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Geo selectors
  function addCountry(countryIref) {
    setCompany((p) => ({
      ...p,
      address: {
        ...p.address,
        city: {
          ...p.address.city,
          iref: "",
          county: {
            ...p.address.city.county,
            iref: "",
            country: { ...p.address.city.county.country, iref: countryIref },
          },
        },
      },
    }));

    const data = client.getData(Config.API_URL_COUNTY, { country: countryIref, orderBy_Name: true });
    setCounties(data?.counties || {});
    setCities({});
  }

  function addCounty(countyIref) {
    setCompany((p) => ({
      ...p,
      address: {
        ...p.address,
        city: {
          ...p.address.city,
          iref: "",
          county: { ...p.address.city.county, iref: countyIref },
        },
      },
    }));

    const data = client.getData(Config.API_URL_CITY, { county: countyIref, orderBy_Name: true });
    setCities(data?.cities || {});
  }

  function addCity(cityIref) {
    setCompany((p) => ({
      ...p,
      address: {
        ...p.address,
        city: { ...p.address.city, iref: cityIref },
      },
    }));

    // fetch city details (name + zipCode -> postalCode)
    const cityData = client.getData(Config.API_URL_CITY + cityIref, {});
    if (!cityData) return;

    setCompany((p) => {
      const next = JSON.parse(JSON.stringify(p));
      if (cityData.name) next.address.city.name = cityData.name;
      if (cityData.county?.name) next.address.city.county.name = cityData.county.name;

      if (cityData.zipCode) {
        const zipCode = String(cityData.zipCode);
        next.address.postalCode = parseInt(zipCode, 10) !== 0 ? zipCode : "";
      } else {
        next.address.postalCode = "";
      }
      return next;
    });
  }

  // Load geo lists when company step becomes visible
  useEffect(() => {
    if (!showCompanyStep) return;

    if (countryId) {
      const data = client.getData(Config.API_URL_COUNTY, { country: countryId, orderBy_Name: true });
      setCounties(data?.counties || {});
    } else {
      setCounties({});
    }

    if (countyId) {
      const data = client.getData(Config.API_URL_CITY, { county: countyId, orderBy_Name: true });
      setCities(data?.cities || {});
    } else {
      setCities({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCompanyStep]);

  async function onValidateOrder() {
    setErrorMsg("");
    clearResult();
    setIdentified(false);
    setInvoiceExists(false);

    if (!canValidate) return;

    setLoading(true);
    try {
      const payload = {
        order: {
          code: orderCode.trim(),
          totalAmount: amount.trim(),
          orderDate: orderDate.trim(),
        },
      };

      const res = validateInvoicePublic(payload);
      const { ok, data } = extract(res);
      if (!ok) throw new Error("INVALID");

      setIdentified(Boolean(data?.identified ?? true));
      lastOrderCodeRef.current = orderCode.trim();

      // If invoice already exists, show download (and keep company step hidden)
      const url = data?.downloadURL || data?.invoiceURL || "";
      if (data?.invoiceExists || url) {
        if (url) {
          setInvoiceExists(true);
          setInvoiceSerial(data?.invoice || "");
          setDownloadUrl(url);
          setExpiresInSeconds(data?.expiresInSeconds ?? null);
        }
      }
    } catch (_) {
      // setErrorMsg(t.errValidate);
      // Silent auto validation: only show error
      // if user did not explicitly click validate
      // (you may also remove this completely)
    } finally {
      setLoading(false);
    }
  }

  async function onGenerateInvoice() {
    setErrorMsg("");
    clearResult();

    if (!canGenerate) return;

    setLoading(true);
    try {
      const payload = {
        order: {
          code: orderCode.trim(),
          totalAmount: amount.trim(),
          orderDate: orderDate.trim(),
          invoice: {
            company: {
              name: company.name.trim(),
              cuid: company.cuid.trim(),
              ruid: company.ruid.trim(),
              phone: company.phone.trim(),
              email: company.email.trim(),
              address: {
                postalCode: company.address.postalCode.trim(),
                street: company.address.street.trim(),
                number: company.address.number.trim(),
                entrance: company.address.entrance.trim(),
                local: company.address.local.trim(),
                floor: company.address.floor.trim(),
                complement: company.address.complement.trim(),
                ...(company.address.city?.iref ? { city: company.address.city.iref } : {}),
              },
            },
          },
        },
      };

      const res = generateInvoicePublic(payload);
      const { ok, data } = extract(res);
      if (!ok) throw new Error("INVALID");

      const url = data?.downloadURL || data?.invoiceURL || "";
      if (!url) throw new Error("INVALID");

      setInvoiceSerial(data?.invoice || "");
      setDownloadUrl(url);
      setExpiresInSeconds(data?.expiresInSeconds ?? null);

      setInvoiceExists(true);
      setIdentified(true);
    } catch (_) {
      setErrorMsg(t.errGenerate);
    } finally {
      setLoading(false);
    }
  }

  const queryCompany = async () => {
    setErrorMsg("");
    if (!identified) return;

    const cui = (company.cuid || "").trim();
    if (!cui) return;

    setLoading(true);
    try {
      // call your gated endpoint (orders) instead of Company ext endpoint
      const payload = {
        order: {
          code: orderCode.trim(),
          totalAmount: amount.trim(),
          orderDate: orderDate.trim(),
        },
        cui,
      };

      const res = client.pushData(Config.API_URL_ORDERS + "invoice/company-lookup/", payload);

      const ok = String(res?.status || "").toUpperCase() === "OK";
      if (!ok) {
        const key = res?.data?.errorKey || "INV_EXTAPI_FAILED";
        setErrorMsg(key === "INV_ORDER_NOT_VERIFIED" ? t.errOrderNotVerified : t.errCompanyLookup);
        return;
      }

      const data = res?.data?.ext; // ext payload from backend
      if (!data || data.cod !== 200) {
        setErrorMsg(t.errCompanyLookup);
        return;
      }

      const comp = data?.found?.[0];

      setCompany((prev) => {
        const u = JSON.parse(JSON.stringify(prev));

        if (comp?.date_generale) {
          u.name = comp.date_generale.denumire || u.name;
          u.ruid = comp.date_generale.nrRegCom || u.ruid;
          u.cuid = String(comp.date_generale.cui || u.cuid);
        }

        if (comp?.adresa_sediu_social) {
          const a = comp.adresa_sediu_social;
          if (a.telefon) u.phone = a.telefon;
          if (a.scod_Postal) u.address.postalCode = a.scod_Postal;

          if (a.sdenumire_Strada) {
            let street = a.sdenumire_Strada;
            const prefix = "Str. ";
            if (street.toLowerCase().startsWith(prefix.toLowerCase())) street = street.slice(prefix.length);
            u.address.street = street;
          }

          if (a.snumar_Strada) u.address.number = a.snumar_Strada;
          if (a.sdetalii_Adresa) u.address.local = a.sdetalii_Adresa;
        }

        // geo ids
        if (data.geo) {
          if (!u.address) u.address = { city: {} };
          if (!u.address.city) u.address.city = {};
          u.address.city.iref = data.geo.city || u.address.city.iref;

          if (!u.address.city.county) u.address.city.county = {};
          u.address.city.county.iref = data.geo.county || u.address.city.county.iref;

          if (!u.address.city.county.country) u.address.city.county.country = {};
          u.address.city.county.country.iref = data.geo.country || u.address.city.county.country.iref;
        }

        return u;
      });

      const country = data?.geo?.country || "";
      const county = data?.geo?.county || "";

      if (country) {
        const cData = client.getData(Config.API_URL_COUNTY, { country, orderBy_Name: true });
        setCounties(cData?.counties || {});
      }
      if (county) {
        const cityData = client.getData(Config.API_URL_CITY, { county, orderBy_Name: true });
        setCities(cityData?.cities || {});
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={pageShellSx}>
      <Box sx={{ width: "100%", maxWidth: 1040 }}>
        <Box sx={pageSurfaceSx}>
          {/* Header */}
          <Box sx={headerSx}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={headerIconSx}
              >
                <ReceiptLongOutlinedIcon />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                  {t.invoice}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(67,20,7,0.68)",
                    display: { xs: "none", sm: "block" },
                    mt: 0.35,
                  }}
                >
                  {t.subtitle}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }} />

              {identified ? (
                <Chip
                  size="small"
                  icon={<CheckCircleOutlineOutlinedIcon />}
                  label={t.validated}
                  sx={{
                    fontWeight: 850,
                    color: "#111111",
                    bgcolor: "#F7B500",
                    "& .MuiChip-icon": { color: "#111111" },
                  }}
                />
              ) : (
                <Chip
                  size="small"
                  icon={<ErrorOutlineOutlinedIcon />}
                  label={t.notValidated}
                  variant="outlined"
                  sx={{
                    fontWeight: 850,
                    color: "#7c2d12",
                    borderColor: "rgba(124,45,18,0.26)",
                    bgcolor: "rgba(255,255,255,0.62)",
                    "& .MuiChip-icon": { color: "#9a3412" },
                  }}
                />
              )}

            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.25, sm: 2, md: 2.5 } }}>
            {/* Result banner */}
            <Box sx={{ mb: 1.25 }}>
              {errorMsg ? <Alert severity="error" sx={{ borderRadius: "8px" }}>{errorMsg}</Alert> : null}

              {!errorMsg && showDownload ? (
                <Alert severity="success" sx={{ borderRadius: "8px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { sm: "center" },
                      gap: { xs: 0.5, sm: 1 },
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography component="span" variant="body2">
                      {t.invoiceReadyLabel} <b>{invoiceSerial || t.readyWord}</b>.
                    </Typography>

                    <Link href={downloadUrl} target="_blank" rel="noreferrer" underline="hover">
                      {t.downloadPdf}
                    </Link>

                    {expiresInSeconds ? (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {t.linkExpires(Math.round(expiresInSeconds / 60))}
                      </Typography>
                    ) : null}
                  </Box>
                </Alert>
              ) : null}
            </Box>

            <Grid container spacing={{ xs: 0.8, sm: 1.5, md: 2 }}>
              {/* Step 1: Order validation (always visible) */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={stepCardSx}>
                  <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900 }}>{t.step1Title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t.step1Desc}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }} />
                      <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "flex-end", sm: "initial" } }}>
                        <Button
                          variant="text"
                          onClick={() => {
                            autoValidatedRef.current = false;
                            setOrderCode("");
                            setAmount("");
                            setOrderDate("");
                            clearAllValidation();
                          }}
                          disabled={loading}
                          sx={{ ...secondaryButtonSx, whiteSpace: "nowrap" }}
                        >
                          {t.clear}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onValidateOrder}
                          disabled={!canValidate || loading}
                          sx={primaryButtonSx}
                        >
                          {loading ? t.working : t.validate}
                        </Button>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 0.8 }} />

                    <Grid container spacing={1.25}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label={t.orderCode}
                          value={orderCode}
                          onChange={(e) => {
                            const next = e.target.value;
                            const prevNormalized = (lastOrderCodeRef.current || "").trim();
                            const nextNormalized = (next || "").trim();
                            if (prevNormalized && nextNormalized && prevNormalized !== nextNormalized) {
                              resetCompanyOnly();
                              lastOrderCodeRef.current = "";
                            }
                            setOrderCode(next);
                            clearAllValidation();
                          }}
                          fullWidth
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label={t.amount}
                          value={amount}
                          onChange={(e) => {
                            setAmount(e.target.value);
                            clearAllValidation();
                          }}
                          fullWidth
                          size="small"
                          inputProps={{ style: { textAlign: "right" } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label={t.orderDate}
                          type="date"
                          value={orderDate}
                          onChange={(e) => {
                            setOrderDate(e.target.value);
                            clearAllValidation();
                          }}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      {identified ? (
                        <Grid item xs={12}>
                          <Alert severity={invoiceExists ? "success" : "info"} sx={{ py: 0.75, borderRadius: "8px" }}>
                            {invoiceExists ? t.step1InfoInvoiceExists : t.step1InfoValidated}
                          </Alert>
                        </Grid>
                      ) : null}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Step 2 + 3: only after validation AND if invoice doesn't already exist */}
              {showCompanyStep ? (
                <>
                  <Grid item xs={12} md={8}>
                    <Card variant="outlined" sx={stepCardSx}>
                      <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
                        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: 900 }}>{t.step2Title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t.required}
                          </Typography>
                        </Stack>

                        <Grid container spacing={1.25}>
                          <Grid item xs={12}>
                            <TextField
                              label={t.companyName}
                              value={company.name}
                              onChange={(e) => setCompany((p) => ({ ...p, name: e.target.value }))}
                              fullWidth
                              required
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <TextField
                              label={t.cui}
                              value={company.cuid}
                              onChange={(e) => setCompany((p) => ({ ...p, cuid: e.target.value }))}
                              fullWidth
                              required
                              size="small"
                            />
                            <IconButton
                              onClick={queryCompany}
                              disabled={loading || !identified || !(company.cuid || "").trim()}
                              title={t.lookupCompany}
                            >
                              <HelpCenterIcon />
                            </IconButton>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label={t.regNo}
                              value={company.ruid}
                              onChange={(e) => setCompany((p) => ({ ...p, ruid: e.target.value }))}
                              fullWidth
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12} sm={8}>
                            <TextField
                              label={t.street}
                              value={company.address.street}
                              onChange={(e) =>
                                setCompany((p) => ({ ...p, address: { ...p.address, street: e.target.value } }))
                              }
                              fullWidth
                              required
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              label={t.no}
                              value={company.address.number}
                              onChange={(e) =>
                                setCompany((p) => ({ ...p, address: { ...p.address, number: e.target.value } }))
                              }
                              fullWidth
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <DropDown
                              name={t.country}
                              value={countryId}
                              doptions={countriesOptions}
                              hasIcons={false}
                              onDark={false}
                              onChangeFunction={addCountry}
                              minWidth={0}
                              maxWidth={9999}
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <DropDown
                              name={t.county}
                              value={countyId}
                              doptions={countiesOptions}
                              hasIcons={false}
                              onDark={false}
                              onChangeFunction={addCounty}
                              disabled={!countryId}
                              minWidth={0}
                              maxWidth={9999}
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <DropDown
                              name={t.city}
                              value={cityId}
                              doptions={citiesOptions}
                              hasIcons={false}
                              onDark={false}
                              onChangeFunction={addCity}
                              disabled={!countyId}
                              minWidth={0}
                              maxWidth={9999}
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              label={t.postal}
                              value={company.address.postalCode}
                              onChange={(e) =>
                                setCompany((p) => ({ ...p, address: { ...p.address, postalCode: e.target.value } }))
                              }
                              fullWidth
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              label={t.email}
                              value={company.email}
                              onChange={(e) => setCompany((p) => ({ ...p, email: e.target.value }))}
                              fullWidth
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              label={t.phone}
                              value={company.phone}
                              onChange={(e) => setCompany((p) => ({ ...p, phone: e.target.value }))}
                              fullWidth
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t.geoHint}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={stepCardSx}>
                      <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
                        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>{t.step3Title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                          {canGenerate ? t.readyToGenerate : t.completeRequired}
                        </Typography>

                        <Stack spacing={1}>
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={!canGenerate || loading}
                            onClick={onGenerateInvoice}
                            sx={primaryButtonSx}
                          >
                            {loading ? t.working : t.generateInvoice}
                          </Button>

                          <Button fullWidth variant="text" onClick={clearResult} disabled={loading} sx={secondaryButtonSx}>
                            {t.clearResult}
                          </Button>

                          {!canGenerate ? (
                            <Alert severity="warning" sx={{ py: 0.75, borderRadius: "8px" }}>
                              {t.warnFill}
                            </Alert>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              ) : null}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
